import type { WritableComputedRef } from 'vue'
import {
  ProductFinderMode,
  type ProductFinderItemFragment,
  type ProductFinderQueryVariables,
} from '#graphql-operations'
import {
  type UseProductFinderType,
  extractFacetsFromQuery,
} from '~/helpers/productFinder'

type UseProductFinder = {
  /**
   * All product results.
   */
  results: ComputedRef<ProductFinderItemFragment[]>

  /**
   * The current search text as a writable computed value.
   */
  searchText: WritableComputedRef<string>

  /**
   * Total amount of products for the current search.
   */
  totalResults: ComputedRef<number>

  /**
   * Total amount of pages for all products.
   */
  totalPages: ComputedRef<number>

  /**
   * The current page, starting at 0.
   */
  currentPage: ComputedRef<number>

  /**
   * The link to load more products.
   */
  loadMoreLink: ComputedRef<string | undefined>

  /**
   * The selected facets.
   */
  selectedFacets: ComputedRef<Record<string, string[]>>

  /**
   * The GraphQL product finder mode.
   */
  mode: ComputedRef<ProductFinderMode>
}

/**
 * Handles the product finder logic.
 *
 * When calling the composable, the results for the given mode (watches or
 * straps) are loaded. The composable also handles fetching the updated
 * results when the state in the URL changes.
 *
 * The following query parameters are respected:
 *   - page - The current page
 *   - text - The fulltext search term
 *   - f:* - Any query parameter starting with f: is treated as a selected facet
 *
 * The selected facets are stored in the following form:
 * f:FACET_ID=FILTER_1___FILTER_2___FILTER_3
 *
 * Terms of the same facet are concatenated using ___.
 */
export default async function (
  type: UseProductFinderType,
): Promise<UseProductFinder> {
  const perPage = 12
  const router = useRouter()
  const route = useRoute()
  const pageQuery = useQueryString('page', '0')
  const textQuery = useQueryString('text', '')
  const modeQuery = useQueryString('mode', '')

  // The selected facets according to the URL state.
  const selectedFacets = computed<Record<string, string[]>>(() =>
    extractFacetsFromQuery(route.query),
  )

  const currentPage = computed<number>(() => {
    const pageNumeric = parseInt(pageQuery.value)
    if (isNaN(pageNumeric)) {
      return 0
    }
    return Math.max(pageNumeric, 0)
  })

  // The product finder mode.
  const mode = computed(() => {
    if (type === 'watches') {
      if (modeQuery.value === ProductFinderMode.Watches_limited) {
        return ProductFinderMode.Watches_limited
      } else if (modeQuery.value === ProductFinderMode.Watches_archived) {
        return ProductFinderMode.Watches_archived
      }

      // Fall back to the default.
      return ProductFinderMode.Watches_actual
    }

    return ProductFinderMode.Straps
  })

  // The reactive variables for the GraphQL query.
  // Whenever these change, the query is updated.
  const graphqlVariables = computed<ProductFinderQueryVariables>(() => ({
    mode: mode.value,
    input: textQuery.value || undefined,
    offset: 0,
    limit: (currentPage.value + 1) * perPage,
    facets: Object.entries(selectedFacets.value).map(([id, filter]) => {
      return {
        id,
        filter,
      }
    }),
  }))

  // Perform the GraphQL query to load the results.
  const { data } = await useAsyncData(
    () =>
      useCachedGraphqlQuery('productFinder', graphqlVariables.value).then(
        (v) => v.data.findProducts,
      ),
    { watch: [graphqlVariables] },
  )

  // The results.
  const results = computed<ProductFinderItemFragment[]>(
    () => data.value?.items || [],
  )

  // The writable computed property to get and set the fulltext search term.
  const searchText = computed<string>({
    get() {
      return textQuery.value
    },
    set(v: string) {
      // Updating the search term triggers a router push to update the term
      // in the query parameter.
      router.push({
        path: route.path,
        query: {
          ...route.query,
          text: v || undefined,
        },
      })
    },
  })

  // Total products for the current query (without pagination).
  const totalResults = computed<number>(() => data.value?.total || 0)

  // The total amount of pages available.
  const totalPages = computed<number>(() => {
    if (!totalResults.value) {
      return 0
    }

    return Math.ceil(totalResults.value / perPage)
  })

  // The link to load more results. It increments the page query parameter
  // by one. If no more results can be loaded, it returns undefined.
  const loadMoreLink = computed(() => {
    if (currentPage.value + 1 >= totalPages.value) {
      return
    }

    return router.resolve({
      path: route.path,
      query: {
        ...route.query,
        page: currentPage.value + 1,
      },
    }).href
  })

  return {
    results,
    searchText,
    totalResults,
    totalPages,
    currentPage,
    loadMoreLink,
    selectedFacets,
    mode,
  }
}
